import React, { FC, useCallback, useMemo, useState } from 'react';
import { CoPayDetail, CoPayType } from '../../../types';
import { ClickAction, DetailsCardType, Feature } from '../../../utils/analytics';
import { analyticsManager } from '../../../utils/analyticManager';
import './index.less';

export interface CoPayColumnProps {
  label: string;
  detail?: CoPayDetail;
  dataSource?: string;
}

export const CO_PAY_TYPE = {
  Flat: 'flat',
  Tiered: 'tiered',
  Messages: 'messages',
} as const satisfies Record<string, CoPayType>;

type CollapsibleCoPayType = typeof CO_PAY_TYPE.Tiered | typeof CO_PAY_TYPE.Messages;

const isCollapsibleType = (type?: CoPayType): type is CollapsibleCoPayType =>
  type === CO_PAY_TYPE.Tiered || type === CO_PAY_TYPE.Messages;

const TRIGGER_LABELS: Record<CollapsibleCoPayType, string> = {
  [CO_PAY_TYPE.Tiered]: 'View tiers',
  [CO_PAY_TYPE.Messages]: 'View values',
};

const ANALYTICS_CARD_TYPE: Record<CollapsibleCoPayType, DetailsCardType> = {
  [CO_PAY_TYPE.Tiered]: DetailsCardType.CoPayTiers,
  [CO_PAY_TYPE.Messages]: DetailsCardType.CoPayMessages,
};

const SingleRowColumn: FC<{ label: string; value: string; modifier: 'empty' | 'flat' }> = ({
  label,
  value,
  modifier,
}) => (
  <div className="copay-column">
    <div className="copay-column__label text-ultra-dark text-left text-strong item-label">
      {label}
    </div>
    <div className="copay-column__rows">
      <div
        className={`copay-column__row copay-column__row--${modifier} text-ultra-dark text-left text-normal item-value`}
      >
        {value}
      </div>
    </div>
  </div>
);

export const CoPayColumn: FC<CoPayColumnProps> = ({ label, detail, dataSource }) => {
  const [expanded, setExpanded] = useState(false);
  const rowsId = `copay-column-rows-${label.toLowerCase().replace(/\s+/g, '-')}`;

  const sortedEntries = useMemo(
    () => [...(detail?.response ?? [])].sort((a, b) => a.index - b.index),
    [detail?.response],
  );

  const type = detail?.type;
  const handleToggle = useCallback(() => {
    setExpanded((prev) => {
      const next = !prev;
      if (isCollapsibleType(type)) {
        analyticsManager.track('vim_chart_item_click', {
          feature: Feature.Eligibility,
          action: next ? ClickAction.Open : ClickAction.Close,
          detailsCardType: ANALYTICS_CARD_TYPE[type],
          app_source_displayed: dataSource,
        });
      }
      return next;
    });
  }, [type, dataSource]);

  if (!detail || sortedEntries.length === 0) {
    return <SingleRowColumn label={label} value="--" modifier="empty" />;
  }

  if (detail.type === CO_PAY_TYPE.Flat) {
    return <SingleRowColumn label={label} value={sortedEntries[0].value} modifier="flat" />;
  }

  // detail.type is now narrowed to CollapsibleCoPayType
  if (!isCollapsibleType(detail.type)) {
    return null;
  }

  const isTiered = detail.type === CO_PAY_TYPE.Tiered;
  const isCollapsible = sortedEntries.length >= 3;

  const renderRow = (entry: { index: number; value: string }) =>
    isTiered ? (
      <div
        key={entry.index}
        className="copay-column__row copay-column__row--tiered text-ultra-dark text-left text-normal item-value"
      >
        <span className="copay-column__row-label">{`Tier ${entry.index}`}</span>
        <span className="copay-column__row-value">{entry.value}</span>
      </div>
    ) : (
      <div
        key={entry.index}
        className="copay-column__row copay-column__row--message text-ultra-dark text-left text-normal item-value"
      >
        {entry.value}
      </div>
    );

  return (
    <div className="copay-column">
      <div className="copay-column__label text-ultra-dark text-left text-strong item-label">
        {label}
      </div>
      {isCollapsible ? (
        <>
          <button
            type="button"
            className="copay-column__trigger"
            aria-expanded={expanded}
            aria-controls={rowsId}
            onClick={handleToggle}
          >
            <span>{TRIGGER_LABELS[detail.type]}</span>
            <i
              className={`copay-column__chevron ${
                expanded ? 'icon-chevron-up' : 'icon-chevron-down'
              }`}
              aria-hidden
            />
          </button>
          {expanded && (
            <div id={rowsId} className="copay-column__rows copay-column__rows--expanded">
              {sortedEntries.map((entry) => renderRow(entry))}
            </div>
          )}
        </>
      ) : (
        <div className="copay-column__rows">{sortedEntries.map((entry) => renderRow(entry))}</div>
      )}
    </div>
  );
};
